/**
 * Meta campaign creation wizard — per-phase, user-driven content (#4277 chunk 2c).
 *
 * Replaces the pre-refactor single-step wizard that dispatched all three campaigns at once with
 * inline LLM/DALL-E content. The user creates one phase at a time (Prospecting first, HiPo
 * once winners surface, Retargeting later), picking images from the cross-platform asset
 * library; the ad copy is generated by the backend creation task and edited inline on the
 * campaign screen afterwards (#5981) — the wizard never blocks on copy.
 *
 * Note on naming (#4277 chunk 4a): backend enum values stay ``TESTING`` / ``SCALING`` /
 * ``RETARGETING`` (no DB migration in this refactor). User-facing labels are demo-aligned:
 * ``TESTING`` → "Prospecting", ``SCALING`` → "HiPo" (matches Google's HiPo type),
 * ``RETARGETING`` → "Retargeting". See ``PHASE_LABELS`` below.
 *
 * ``phase`` is a required search param — the entry-point at ``/new/campaign`` passes it. The
 * type-first picker (chunk 4b) maps Prospecting → ``TESTING``, HiPo → ``SCALING``, Retargeting →
 * ``RETARGETING``.
 *
 * Three steps once the phase is known (#5981 removed the blocking COMPOSE_ADS copy step):
 *   1. CAMPAIGN_DETAILS — offer URL, page ID, daily budget, optional target CPL.
 *   2. TARGET_LOCATIONS — where the ads run (#5907): the same CampaignLocations component the
 *      Google wizards use, seeded from the business's locations; picks resolve to Meta geo
 *      targeting server-side.
 *   3. PICK_ASSETS — multi-select from the org's `CreativeAsset` library; CREATE dispatches from
 *      here with assets only. One ad per picked asset, copy generated in the background.
 *
 * On dispatch we keep the user on this page and show the "Starting…" card; once the backend
 * commits the campaign row we navigate to /campaigns/meta/{id} where the generating banner lives.
 */

import { Spinner } from '@/components/glitch-ui/spinner';
import { useTestMode } from '@/hooks/useTestMode';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MetaAssetSelect } from '@/components/glitch-ui/meta-asset-select';
import { StepsIndicator } from '@/components/glitch-ui/steps-indicator';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { ImageGallery } from '@/components/glitch-ui/image-gallery';
import { useToast } from '@/hooks/useToast';
import {
  metaConversionEventsQueryOptions,
  metaIntegrationStatusQueryOptions,
  metaLeadgenFormsQueryOptions,
  useCreateMetaPhaseCampaignMutation,
} from '@/lib/query-options';
import { useMetaPagePicker, useMetaPixelPicker } from '@/hooks/useMetaAssetPicker';
import { META_MAX_CREATIVES, parseMetaWizardSearch, toggleCreativeSelection } from '@/lib/meta';
import { MetaCampaignConflictError } from '@/lib/apis';
import type { MetaCampaignOutcome, MetaConversionEventOption, CampaignProximityInput, Location, MetaAdCreativeInput, MetaCampaignType } from '@/lib/types';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';
import { CampaignLocations, type CampaignLocationsRef } from '@/components/campaign/onboarding/campaign-onboarding-locations';
import { businessQueryOptions } from '@/lib/query-options';
import { organizationDataAtom } from '@/store';
import { useAtomValue } from 'jotai';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';

export const Route = createFileRoute('/_authenticated/new/_layout/campaign/meta')({
  // Wrap in withOrgReset (like the Brand wizard) so an org switch mid-wizard remounts and clears
  // org-specific state (locations, selectedAssetIds) instead of submitting another org's ids (#6163 P1-12).
  component: withOrgReset(MetaCampaignWizard),
  // ``phase`` is required (set by ``/new/campaign``); ``offer_url`` is an optional pre-fill from the
  // Meta detail-panel "Try Again" button. See ``components/campaign/meta/meta-campaign-detail-panel.tsx``.
  validateSearch: (search): { phase: MetaCampaignType; offer_url?: string } => parseMetaWizardSearch(search),
});

enum META_STEP {
  CAMPAIGN_DETAILS = 'CAMPAIGN_DETAILS',
  TARGET_LOCATIONS = 'TARGET_LOCATIONS',
  PICK_ASSETS = 'PICK_ASSETS',
}

const STEP_ORDER: META_STEP[] = [
  META_STEP.CAMPAIGN_DETAILS,
  META_STEP.TARGET_LOCATIONS,
  META_STEP.PICK_ASSETS,
];

// Backend enum values stay TESTING/SCALING/RETARGETING (no DB migration). Display labels are
// demo-aligned: see file header.
const PHASE_LABELS: Record<MetaCampaignType, string> = {
  SCALING: 'High Performance',
  TESTING: 'Prospecting',
  RETARGETING: 'Retargeting',
};


// CTA button options offered for lead/conversion campaigns (#4655), value → display label.
function MetaCampaignWizard() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { toast } = useToast();
  const isTestMode = useTestMode();
  const { phase, offer_url: prefilledOfferUrl } = Route.useSearch();

  // Pre-flight: Meta must be connected and an ad account must be selected, else direct the user to
  // Settings → Integrations.
  const { data: integrationStatus, isLoading: isLoadingIntegration } = useQuery({
    ...metaIntegrationStatusQueryOptions(),
    enabled: isTestMode,
  });
  // Step + form state. ``phase`` is fixed by the route's search param — the entry-point at
  // ``/new/campaign`` chose it.
  const [step, setStep] = useState<META_STEP>(META_STEP.CAMPAIGN_DETAILS);
  // What the campaign should get the client (#6186) — expands to the full Meta API stack
  // server-side. Website leads is the build-first default from the objectives reference.
  const [outcome, setOutcome] = useState<MetaCampaignOutcome>('WEBSITE_LEADS');
  // The published instant form for INSTANT_FORM_LEADS (#6186); tell-on-click error (#5857).
  const [leadgenFormId, setLeadgenFormId] = useState<string | null>(null);
  const [leadgenFormError, setLeadgenFormError] = useState<string | undefined>(undefined);
  // Creation-time optimization-event pick (#6076) — null = the objective's default standard event.
  // Meta makes conversion settings immutable once the ad set publishes (subcode 3260011), so the
  // wizard is the ONLY place to choose; the Conversions tab renders it display-only afterwards.
  const [optimizationEvent, setOptimizationEvent] = useState<MetaConversionEventOption | null>(null);
  const [offerUrl, setOfferUrl] = useState(prefilledOfferUrl ?? '');
  // Page + Pixel are bound up-front in the pre-flight account-setup gate (#4652) and persisted on the
  // MetaAdAccount row, so the wizard body reads them straight off integrationStatus when needed.
  // Pre-flight account-setup gate state (#4652), mirroring the Google Ads account-selection gate.
  // The Google gate latches "is this prerequisite already satisfied?" once at entry (its data comes
  // from a suspense query it can read synchronously); our integration status is a non-suspense query,
  // so we latch the same decision in a ref the first time it resolves. ``setupAcknowledged`` is the
  // CONTINUE click — so the user explicitly advances (not an auto-skip), exactly like Google.
  const setupNeededAtEntry = useRef<boolean | null>(null);
  if (setupNeededAtEntry.current === null && integrationStatus?.selected_ad_account) {
    const acct = integrationStatus.selected_ad_account;
    setupNeededAtEntry.current = !acct.page_id || !acct.pixel_id;
  }
  const [setupAcknowledged, setSetupAcknowledged] = useState(false);
  // Location step (#5907) — the same CampaignLocations component the six Google wizards use,
  // seeded from the business's saved locations. Selections travel as Location cache criteria ids
  // + Radius-tab entries; the backend resolver turns them into Meta geo targeting at push time.
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: business } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [radii, setRadii] = useState<CampaignProximityInput[]>([]);
  const [locationsStepValid, setLocationsStepValid] = useState(false);
  const campaignLocationsRef = useRef<CampaignLocationsRef>(null);
  // Seed the location step from the business ONCE (#6163 P1-8). The old effect re-seeded on any
  // business refetch while the selection was empty, restoring locations the user had just cleared.
  // Belt-and-braces (#6179): only seed while the selection is still empty, so a late-resolving
  // business query can never overwrite locations added manually before the first seed.
  const locationsSeededRef = useRef(false);
  useEffect(() => {
    const seed = business?.target_locations;
    if (!locationsSeededRef.current && seed) {
      locationsSeededRef.current = true;
      setLocations((current) => (current.length === 0 ? seed : current));
    }
  }, [business?.target_locations]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);
  // Ads keyed by asset id so re-picking an image preserves its copy across step 3 ↔ 4 navigation.
  // Tell-on-click validation (#5857): NEXT/CREATE stay enabled; clicking with missing typed input
  // marks the offending field(s) instead of sitting silently disabled.
  const [offerUrlError, setOfferUrlError] = useState<string | undefined>(undefined);
  // When true, empty required copy fields on the compose step render as errors. Editing a field
  // (making it non-empty) clears its error automatically since errors derive from emptiness.
  // A 409 conflict from the last submit — a creation for this offer is already running (#5982:
  // an existing campaign no longer blocks). Shown inline rather than as a failure toast (#5742 M-2).
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const create = useCreateMetaPhaseCampaignMutation();

  // #4288 Phase 2c — wizard no longer collects Daily Budget. The campaign is created with the
  // Meta-minimum placeholder (``100`` cents == 1 unit of currency, Meta's API floor); the user
  // sets the real value in Settings → Budget → Meta Ads (the per-campaign / per-ad-set sliders)
  // before launching. Mirrors Google's "create with budget=0, allocate later" pattern from
  // ``app.campaign.router``. ``daily_budget_cents > 0`` is still enforced server-side for create.
  const PLACEHOLDER_DAILY_BUDGET_CENTS = 100;
  // Page is guaranteed by the pre-flight account-setup gate (#4652); step 1 only needs the offer URL.
  const detailsValid = offerUrl.trim() !== '' && (outcome !== 'INSTANT_FORM_LEADS' || leadgenFormId != null);
  const assetsValid = selectedAssetIds.length > 0;

  // Drop a stale duplicate-phase conflict once the user changes what they'd submit.
  useEffect(() => {
    setConflictMessage(null);
  }, [offerUrl, phase]);

  // Assets-only since #5981: the copy the COMPOSE_ADS step used to collect is generated by the
  // backend creation task, and edited inline on the campaign screen afterwards.
  const ads: MetaAdCreativeInput[] = useMemo(
    () => selectedAssetIds.map((id) => ({ creative_asset_id: id })),
    [selectedAssetIds],
  );

  const handleSubmit = () => {
    // Details and assets are guaranteed by the earlier steps' own gates.
    if (!detailsValid || !assetsValid) return;
    setConflictMessage(null);
    create.mutate(
      {
        phase,
        offer_url: offerUrl.trim(),
        daily_budget_cents: PLACEHOLDER_DAILY_BUDGET_CENTS,
        ads,
        outcome,
        leadgen_form_id: outcome === 'INSTANT_FORM_LEADS' ? leadgenFormId : null,
        optimization_event: optimizationEvent,
        // Target CPL is set post-create on the Budget tab's Bidding panel (#5981) — one home for
        // the field instead of wizard + detail page both editing it.
        target_cpl_cents: null,
        // #5907: the location step's picks. The backend resolves them to Meta geo targeting;
        // an empty selection never reaches here (the step gates NEXT on at least one).
        target_location_criteria_ids: locations.map((location) => location.criteria_id),
        target_radii: radii.length > 0 ? radii : null,
      },
      {
        // The router creates the campaign row synchronously (#5981), so the dispatch response
        // carries its slug — land on the campaign screen immediately, where the generating-progress
        // card renders. Google parity: no interstitial. No success toast either — the navigation is
        // the success signal.
        onSuccess: (data) => {
          navigate({
            to: '/campaigns/meta/$metaCampaignSlug',
            params: { metaCampaignSlug: data.slug },
            replace: true,
          });
        },
        onError: (error) => {
          // A 409 (a creation for this offer is already running) is an expected conflict, not a
          // failure — surface it inline on this step instead of a destructive toast (#5742 M-2).
          if (error instanceof MetaCampaignConflictError) {
            setConflictMessage(error.message);
            return;
          }
          toast({
            title: 'Could not start creation',
            description: error instanceof Error ? error.message : 'Please try again.',
            variant: 'destructive',
          });
        },
      },
    );
  };


  const currentStepIndex = STEP_ORDER.indexOf(step) + 1;

  const goNext = () => {
    // Tell-on-click: the details step's NEXT stays enabled; clicking with an empty Offer URL
    // marks the field and doesn't advance.
    if (step === META_STEP.CAMPAIGN_DETAILS && !detailsValid) {
      // Tell-on-click (#5857): mark exactly what's missing instead of a silently dead NEXT.
      if (offerUrl.trim() === '') setOfferUrlError('Required');
      if (outcome === 'INSTANT_FORM_LEADS' && leadgenFormId == null) setLeadgenFormError('Required');
      return;
    }
    // Leaving the location step commits the selection into `locations` (and persists any
    // approved business-location candidates) — same approveLocations contract as brand.tsx.
    if (step === META_STEP.TARGET_LOCATIONS && !campaignLocationsRef.current?.approveLocations()) {
      return;
    }
    const nextIdx = STEP_ORDER.indexOf(step) + 1;
    if (nextIdx < STEP_ORDER.length) setStep(STEP_ORDER[nextIdx]);
  };
  const goBack = () => {
    const prevIdx = STEP_ORDER.indexOf(step) - 1;
    if (prevIdx >= 0) setStep(STEP_ORDER[prevIdx]);
    else navigate({ to: '/new/campaign' });
  };

  // #6257: Meta UI is gated behind test mode until Meta App Review clears (the pre-GA gate from
  // #5742 B3, revived with the test-mode check). The backend keeps its own flag regardless.
  if (!isTestMode) {
    return (
      <PageShell>
        <Card className="p-6">
          <p className="text-center text-gray-700">Meta campaigns aren't available for this organization yet.</p>
        </Card>
      </PageShell>
    );
  }

  if (isLoadingIntegration) {
    return (
      <PageShell>
        <Card className="p-6">
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" className="text-gray-400 mr-2" />
            <p className="text-gray-500">Checking Meta connection…</p>
          </div>
        </Card>
      </PageShell>
    );
  }

  // Empty state — Meta not yet connected.
  if (!integrationStatus?.is_connected || !integrationStatus.selected_ad_account) {
    return (
      <PageShell>
        <Card className="rounded-lg border bg-white">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-gray-900 mb-4" strokeWidth={1} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect Meta First</h3>
              <p className="text-gray-700 mb-6">
                {integrationStatus?.is_connected
                  ? 'Meta is connected. Pick an ad account in Settings → Integrations before creating campaigns.'
                  : 'You need to connect a Meta Business account before creating Meta campaigns.'}
              </p>
              <Button
                onClick={() => navigate({ to: '/settings/integrations' })}
                size="lg"
                className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
              >
                <span>GO TO INTEGRATIONS</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // Pre-flight account-setup gate (#4652). A Meta campaign needs two account-level prerequisites
  // bound before any ad set can be created: a Facebook Page (ads run *as* a Page) and a Pixel (the
  // conversions OUTCOME_LEADS objective every wizard phase uses optimises toward Pixel-measured
  // events — the campaign service raises without one, see `_build_promoted_object`). Page was
  // previously a step-1 field, Pixel was only enforced at task time. This single gate mirrors the
  // Google Ads account-selection gate at /new/campaign exactly: shown only when the prerequisite is
  // missing at entry, both pickers in one Card, an explicit CONTINUE (disabled until both are set),
  // and skipped entirely for a returning account where both are already bound.
  if (setupNeededAtEntry.current && !setupAcknowledged) {
    const acct = integrationStatus.selected_ad_account;
    return (
      <MetaAdAccountSetupStep
        metaAdAccountId={acct.id ?? null}
        currentPageId={acct.page_id ?? null}
        currentPageName={acct.page_name ?? null}
        hasPagesScope={(integrationStatus.scopes ?? []).includes('pages_show_list')}
        currentPixelId={acct.pixel_id ?? null}
        currentPixelName={acct.pixel_name ?? null}
        isValid={!!acct.page_id && !!acct.pixel_id}
        onBack={() => navigate({ to: '/new/campaign' })}
        onContinue={() => setSetupAcknowledged(true)}
      />
    );
  }

  // Details step: NEXT stays enabled (tell-on-click marks the Offer URL on click). Asset picking
  // and the location step are selection-gated, so they keep the disabled gate (the location
  // step's validity comes from the component: at least one location or radius).
  const nextEnabled =
    step === META_STEP.PICK_ASSETS ? assetsValid : step === META_STEP.TARGET_LOCATIONS ? locationsStepValid : true;

  return (
    <PageShell wide={step === META_STEP.TARGET_LOCATIONS || step === META_STEP.PICK_ASSETS}>
      <StepsIndicator currentStep={currentStepIndex} totalSteps={STEP_ORDER.length} />
      {/* Stage-aware title (#5979): the locations and assets steps ask a plain question (locations
          copy matches the Google wizards' step); the first step keeps the campaign-type heading. The
          per-step question also stands in for an in-content subheading (e.g. the old "Asset Library"). */}
      <h1 className="text-3xl font-bold text-center mb-8 mt-4">
        {step === META_STEP.TARGET_LOCATIONS
          ? 'Where will your ads show?'
          : step === META_STEP.PICK_ASSETS
            ? 'Which assets will your ads use?'
            : `Create a Meta ${PHASE_LABELS[phase]} Campaign`}
      </h1>

      {step === META_STEP.CAMPAIGN_DETAILS && (
        <CampaignDetailsStep
          offerUrl={offerUrl}
          setOfferUrl={(v) => {
            setOfferUrl(v);
            setOfferUrlError(undefined); // clear the error as soon as the field is edited
          }}
          outcome={outcome}
          setOutcome={(value) => {
            setOutcome(value);
            // The event picker only applies to website (Pixel-backed) outcomes, and the form only
            // to instant forms — clear whichever the new outcome makes meaningless (422 otherwise).
            // Any outcome change resets the event pick: instant forms take none, and website
            // outcomes have objective-scoped catalogs (#6186) — a Leads event under Sales 422s.
            setOptimizationEvent(null);
            if (value !== 'INSTANT_FORM_LEADS') {
              setLeadgenFormId(null);
              setLeadgenFormError(undefined);
            }
          }}
          optimizationEvent={optimizationEvent}
          setOptimizationEvent={setOptimizationEvent}
          leadgenFormId={leadgenFormId}
          setLeadgenFormId={(value) => {
            setLeadgenFormId(value);
            setLeadgenFormError(undefined);
          }}
          leadgenFormError={leadgenFormError}
          metaAdAccountId={integrationStatus?.selected_ad_account?.id ?? null}
          offerUrlError={offerUrlError}
        />
      )}
      {step === META_STEP.TARGET_LOCATIONS && (
        <Card className="p-6">
          <CampaignLocations
            ref={campaignLocationsRef}
            onNext={() => {}}
            setLocations={setLocations}
            setStepValid={setLocationsStepValid}
            locations={locations}
            businessId={business?.id}
            candidates={business?.target_locations ?? []}
            setRadii={setRadii}
          />
        </Card>
      )}
      {step === META_STEP.PICK_ASSETS && (
        <PickAssetsStep
          selectedAssetIds={selectedAssetIds}
          // One ad per image; the backend caps a phase at META_MAX_CREATIVES ads, so a pick past the
          // cap is a no-op (the count + note in the step tell the user why) (#5742 M-1).
          onToggleAsset={(asset) => setSelectedAssetIds((prev) => toggleCreativeSelection(prev, asset.id))}
        />
      )}

      {conflictMessage && step === META_STEP.PICK_ASSETS && (
        <div className="mt-6 border-l-4 border-[#C4B5FD] bg-[#EDE9FE] text-[#290BD9] p-3 text-sm">
          {conflictMessage}
        </div>
      )}

      {/* Same centered band as the Google wizards' button row (#5979): on the wide locations
          step the buttons stay together instead of spreading to the shell edges; on the normal
          60% column this width is a no-op. */}
      <div className="mx-auto flex w-full max-w-[614px] justify-between items-center pt-6">
        <Button
          variant="outline"
          size="lg"
          onClick={goBack}
          disabled={create.isPending}
          className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>BACK</span>
        </Button>

        {step === META_STEP.PICK_ASSETS ? (
          <Button
            size="lg"
            disabled={create.isPending || !assetsValid}
            onClick={handleSubmit}
            className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
          >
            {create.isPending ? (
              <>
                <Spinner size="sm" />
                <span>STARTING…</span>
              </>
            ) : (
              <>
                <span>CREATE</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        ) : (
          <Button
            size="lg"
            disabled={!nextEnabled}
            onClick={goNext}
            className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
          >
            <span>NEXT</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-5xl w-full space-y-6 m-auto pt-8 pb-16">
      <div className="flex justify-center">
        {/* wide: the locations step's map + checklist need the full shell (#5979) — the 60%
            column that fits the form steps crams them. */}
        <div className={wide ? 'w-full px-4' : 'w-[60%]'}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Pre-flight account-setup gate for the Meta campaign wizard (#4652).
 *
 * A Meta campaign needs two account-level prerequisites bound before any ad set can be created: a
 * Facebook Page (ads run *as* a Page) and a Pixel (the conversions OUTCOME_LEADS objective every
 * wizard phase uses optimises toward Pixel-measured events). Both pickers live on one page here.
 *
 * Styling + behaviour deliberately mirror the Google Ads account-selection gate at
 * ``routes/.../new/_layout/campaign/index.tsx`` exactly: same ``max-w-3xl`` page shell (not the
 * wizard's wider ``PageShell``), same centered ``mb-8`` heading, same BACK + CONTINUE button row with
 * CONTINUE disabled until the step is valid. Each picker persists its selection to the
 * ``MetaAdAccount`` row; CONTINUE (enabled once both are set) advances into the wizard.
 * ``MetaPagePicker`` carries its own missing-``pages_show_list``-scope reconnect path.
 */
function MetaAdAccountSetupStep({
  metaAdAccountId,
  currentPageId,
  currentPageName,
  hasPagesScope,
  currentPixelId,
  currentPixelName,
  isValid,
  onBack,
  onContinue,
}: {
  metaAdAccountId: number | null;
  currentPageId: string | null;
  currentPageName: string | null;
  hasPagesScope: boolean;
  currentPixelId: string | null;
  currentPixelName: string | null;
  isValid: boolean;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="max-w-3xl w-full space-y-6 mx-auto pt-8 pb-12 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">Finish Setting Up Your Ad Account</h1>
        <Card className="p-6">
          <div className="space-y-6">
            <MetaPagePicker
              metaAdAccountId={metaAdAccountId}
              currentPageId={currentPageId}
              currentPageName={currentPageName}
              hasPagesScope={hasPagesScope}
            />
            <MetaPixelPicker
              metaAdAccountId={metaAdAccountId}
              currentPixelId={currentPixelId}
              currentPixelName={currentPixelName}
            />
          </div>
        </Card>
        <div className="flex justify-between items-center pt-6">
          <Button
            variant="outline"
            size="lg"
            onClick={onBack}
            className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>BACK</span>
          </Button>
          <Button
            size="lg"
            disabled={!isValid}
            onClick={onContinue}
            className="w-[216px] py-6 bg-[#290BD9] hover:bg-[#2309C5] shadow-lg flex items-center justify-center space-x-2"
          >
            <span>CONTINUE</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * In-gate Meta Pixel picker (#4652). Mirrors :func:`MetaPagePicker`: selecting a Pixel persists
 * ``pixel_id``/``pixel_name`` to the ``MetaAdAccount`` row (``POST /meta/ad-accounts/select-pixel``)
 * and invalidates the integration-status query so the gate advances. The bound Pixel drives the
 * conversions ``promoted_object`` — an OUTCOME_LEADS campaign won't create until one is set. No scope
 * branch (unlike Pages): Pixels read via the always-granted ``ads_management`` / ``ads_read`` scopes.
 */
function MetaPixelPicker({
  metaAdAccountId,
  currentPixelId,
  currentPixelName,
}: {
  metaAdAccountId: number | null;
  currentPixelId: string | null;
  currentPixelName: string | null;
}) {
  const { pixels, isLoading, isPending, onChange: handlePixelChange } = useMetaPixelPicker({
    metaAdAccountId,
    currentPixelId,
  });

  return (
    <div className="space-y-1.5">
      <Label htmlFor="meta-pixel-select">Meta Pixel</Label>
      {isLoading ? (
        <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
          <Spinner size="sm" /> Loading Pixels…
        </div>
      ) : pixels.length === 0 ? (
        <div className="bg-[#FEF3C7] border-l-4 border-l-[#A16207] rounded-md p-4">
          <p className="text-sm text-gray-900 font-medium">No Meta Pixels found on this account</p>
          <p className="text-sm text-gray-700 mt-1">
            Create one in Meta Events Manager, then refresh the connection from Settings → Integrations.
          </p>
        </div>
      ) : (
        <MetaAssetSelect
          id="meta-pixel-select"
          value={currentPixelId}
          onValueChange={handlePixelChange}
          disabled={isPending}
          placeholder={currentPixelName ?? 'Select a Meta Pixel'}
          options={pixels.map((pixel) => ({
            value: pixel.id,
            label: (
              <div className="flex flex-col">
                <span className="text-sm text-gray-900">{pixel.name || 'Unnamed Pixel'}</span>
                <span className="text-xs text-gray-500">{pixel.id}</span>
              </div>
            ),
          }))}
        />
      )}
    </div>
  );
}

// The simplified outcome picker (#6186) — one question, each answer hard-codes the full Meta API
// stack server-side. Meta's raw objective tree (Awareness / Traffic / Engagement / App promotion)
// is deliberately not exposed; Conversations (WhatsApp / Messenger) is phase 2. Source of truth:
// the campaign-objectives appendix in docs/specifications/meta_implementation_report.md.
const OUTCOME_OPTIONS: { value: MetaCampaignOutcome; label: string; description: string }[] = [
  {
    value: 'WEBSITE_LEADS',
    label: 'Leads from your website',
    description: 'Collect leads on your site, measured by your Meta Pixel.',
  },
  {
    value: 'INSTANT_FORM_LEADS',
    label: 'Leads from an instant form',
    description: 'Collect leads in a form that opens right on Facebook and Instagram.',
  },
  {
    value: 'WEBSITE_SALES',
    label: 'Sales on your website',
    description: 'Find people likely to purchase, measured by your Meta Pixel.',
  },
];

function CampaignDetailsStep({
  offerUrl,
  setOfferUrl,
  outcome,
  setOutcome,
  optimizationEvent,
  setOptimizationEvent,
  leadgenFormId,
  setLeadgenFormId,
  leadgenFormError,
  metaAdAccountId,
  offerUrlError,
}: {
  offerUrl: string;
  setOfferUrl: (v: string) => void;
  outcome: MetaCampaignOutcome;
  setOutcome: (v: MetaCampaignOutcome) => void;
  optimizationEvent: MetaConversionEventOption | null;
  setOptimizationEvent: (v: MetaConversionEventOption | null) => void;
  leadgenFormId: string | null;
  setLeadgenFormId: (v: string | null) => void;
  /** Tell-on-click (#5857): set when NEXT was clicked on the instant-form outcome with no form picked. */
  leadgenFormError?: string;
  metaAdAccountId: number | null;
  /** Tell-on-click (#5857): set when NEXT was clicked with an empty Offer URL. */
  offerUrlError?: string;
}) {
  const isWebsiteConversionOutcome = outcome === 'WEBSITE_LEADS' || outcome === 'WEBSITE_SALES';
  // The account catalog (standard Pixel events + custom conversions) — only fetched while a
  // website (Pixel-backed) outcome is selected; the picker is hidden otherwise.
  const { data: eventCatalog = [] } = useQuery({
    // Outcome-scoped (#6186): Meta only accepts objective-matched events (subcode 2446814).
    ...metaConversionEventsQueryOptions(outcome),
    enabled: isWebsiteConversionOutcome,
  });
  // The bound Page's published instant forms (#6186) — only fetched for the form outcome.
  const { data: leadgenForms = [], isLoading: isLoadingForms } = useQuery({
    ...metaLeadgenFormsQueryOptions(metaAdAccountId),
    enabled: metaAdAccountId != null && outcome === 'INSTANT_FORM_LEADS',
  });
  const eventKey = (option: { type: string; event?: string | null; custom_conversion_id?: string | null }) =>
    option.type === 'standard' ? `standard:${option.event}` : `custom:${option.custom_conversion_id}`;
  const defaultEventName = outcome === 'WEBSITE_SALES' ? 'Purchase' : 'Lead';
  // The Facebook Page is bound up-front in the pre-flight account-setup gate (#4652), so it's no
  // longer collected here — the wizard only reaches this step once a Page (and Pixel) is set.
  return (
    <Card className="p-6">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="offer-url-input">Offer URL</Label>
            <FieldError message={offerUrlError} />
          </div>
          <p className="text-xs text-gray-500">The landing page the ads will drive traffic to.</p>
          <div className={offerUrlError ? 'relative rounded-md ring-1 ring-red-500' : 'relative'}>
            <BusinessLandingPageCombobox
              selectedUrl={offerUrl || null}
              onSelect={(entry) => setOfferUrl(entry?.url ?? '')}
              placeholder="https://example.com/landing-page"
              inputId="offer-url-input"
            />
            <InputAdornment error={!!offerUrlError} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label id="objective-label">What should this campaign get you?</Label>
          <p className="text-xs text-gray-500">Each option configures the full Meta delivery setup.</p>
          <RadioGroup
            value={outcome}
            onValueChange={(value) => setOutcome(value as MetaCampaignOutcome)}
            className="space-y-3 pt-1"
            aria-labelledby="objective-label"
          >
            {OUTCOME_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-start space-x-3">
                <RadioGroupItem value={option.value} id={`objective-${option.value}`} className="mt-1" />
                <Label htmlFor={`objective-${option.value}`} className="cursor-pointer font-normal">
                  <span className="block font-semibold text-gray-900">{option.label}</span>
                  <span className="block text-sm text-gray-500">{option.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        {outcome === 'INSTANT_FORM_LEADS' && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="leadgen-form-select">Instant Form</Label>
              <FieldError message={leadgenFormError} />
            </div>
            <p className="text-xs text-gray-500">
              The published form leads are collected in. Created and managed in Meta Ads Manager.
            </p>
            {isLoadingForms ? (
              <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
                <Spinner size="sm" /> Loading forms…
              </div>
            ) : leadgenForms.length === 0 ? (
              <div className="bg-[#FEF3C7] border-l-4 border-l-[#A16207] rounded-md p-4">
                <p className="text-sm text-gray-900 font-medium">No published instant forms on this Page</p>
                <p className="text-sm text-gray-700 mt-1">
                  Create and publish one in Meta Ads Manager (Ads Manager → Instant Forms), then return here.
                </p>
              </div>
            ) : (
              <div className={leadgenFormError ? 'rounded-md ring-1 ring-red-500' : undefined}>
                <MetaAssetSelect
                  id="leadgen-form-select"
                  value={leadgenFormId}
                  onValueChange={setLeadgenFormId}
                  placeholder="Select an instant form"
                  options={leadgenForms.map((form) => ({
                    value: form.id,
                    label: (
                      <>
                        {form.name || 'Unnamed form'}
                        <span className="text-gray-500"> · {form.id}</span>
                      </>
                    ),
                  }))}
                />
              </div>
            )}
          </div>
        )}
        {isWebsiteConversionOutcome && (
          <div className="space-y-1.5">
            <Label htmlFor="optimization-event-select">Optimization Event</Label>
            <p className="text-xs text-gray-500">
              The Pixel event ad delivery optimizes toward. Changing it later recreates the ad set and restarts delivery learning.
            </p>
            <Select
              value={optimizationEvent ? eventKey(optimizationEvent) : 'objective-default'}
              onValueChange={(value) => {
                if (value === 'objective-default') {
                  setOptimizationEvent(null);
                  return;
                }
                const option = eventCatalog.find((entry) => eventKey(entry) === value);
                setOptimizationEvent(option ?? null);
              }}
            >
              <SelectTrigger id="optimization-event-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="objective-default">{defaultEventName} (recommended default)</SelectItem>
                {eventCatalog
                  .filter((option) => eventKey(option) !== `standard:${defaultEventName.toUpperCase()}`)
                  .map((option) => (
                    <SelectItem key={eventKey(option)} value={eventKey(option)}>
                      {option.name}
                      {option.type === 'custom' ? ' (custom conversion)' : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {/*
          Target CPL moved to the campaign page's Budget → Bidding Strategy panel (#5981) — one
          home for the field instead of wizard + detail page both editing it. The dispatch sends
          target_cpl_cents: null and the user sets it (any time, drafts included) after creation.
        */}
        {/*
          Phase 2c — Daily Budget moved to Settings → Budget → Meta Ads (per-campaign / per-ad-set
          sliders). The campaign is created with a Meta-minimum placeholder so the API accepts the
          create call; the user sets the real daily spend after creation, before launching. Mirrors
          how Google campaigns work — created with ``budget=0``, allocated via Manage Budget.
        */}
      </div>
    </Card>
  );
}

function PickAssetsStep({
  selectedAssetIds,
  onToggleAsset,
}: {
  selectedAssetIds: number[];
  onToggleAsset: (asset: { id: number }) => void;
}) {
  const atCap = selectedAssetIds.length >= META_MAX_CREATIVES;
  return (
    <Card className="p-6">
      <div className="space-y-4">
        <p className="text-sm text-gray-700 text-center">
          One ad is created per image, up to {META_MAX_CREATIVES}.
        </p>
        {atCap && (
          <div className="border-l-4 border-[#FDE68A] bg-[#FEF3C7] text-[#A16207] p-3 text-sm">
            Maximum of {META_MAX_CREATIVES} ads per campaign reached. Deselect an image to choose a different one.
          </div>
        )}
        <ImageGallery
          title=""
          baseText=""
          pageSize={50}
          mode="picker"
          selectedAssetIds={selectedAssetIds}
          onSelect={(asset) => onToggleAsset({ id: asset.id })}
        />
        <p className="text-right text-sm text-gray-500">
          {selectedAssetIds.length} of {META_MAX_CREATIVES} selected
        </p>
      </div>
    </Card>
  );
}

function MetaPagePicker({
  metaAdAccountId,
  currentPageId,
  currentPageName,
  hasPagesScope,
}: {
  metaAdAccountId: number | null;
  currentPageId: string | null;
  currentPageName: string | null;
  /** ``true`` iff the integration token carries the ``pages_show_list`` scope (chunk 3b). */
  hasPagesScope: boolean;
}) {
  const { pages, isLoading: pagesLoading, isPending, onChange: handleChange } = useMetaPagePicker({
    metaAdAccountId,
    currentPageId,
    hasPagesScope,
  });

  return (
    <div className="space-y-1.5">
      <Label htmlFor="meta-page-select">Facebook Page</Label>
      <MetaAssetSelect
        id="meta-page-select"
        value={currentPageId}
        onValueChange={handleChange}
        disabled={!metaAdAccountId || !hasPagesScope || pagesLoading || isPending}
        placeholder={pagesLoading ? 'Loading Pages…' : 'Pick a Page'}
        triggerValue={currentPageName ?? currentPageId ?? undefined}
        options={(pages ?? []).map((page) => ({
          value: page.id,
          label: `${page.name}${page.category ? ` · ${page.category}` : ''}`,
        }))}
        emptyContent={
          hasPagesScope && !pagesLoading ? (
            <div className="px-2 py-1.5 text-xs text-gray-500">No Facebook Pages found.</div>
          ) : undefined
        }
      />
      {!hasPagesScope ? (
        <p className="text-xs text-gray-700">
          Your Meta connection doesn&apos;t include Page access. Disconnect and reconnect Meta in
          Settings → Integrations to grant the missing permission.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Your ads run as this Page. Saved as the default for this ad account on change. Manage in
          Settings → Integrations.
        </p>
      )}
    </div>
  );
}
